import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Wallet, 
  PiggyBank, 
  Heart, 
  Plus, 
  ArrowRightLeft,
  Lock,
  Unlock,
  Clock,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POT_ICONS = {
  spend: { icon: Wallet, color: 'text-blue-500', bg: 'bg-blue-100' },
  save: { icon: PiggyBank, color: 'text-emerald-500', bg: 'bg-emerald-100' },
  give: { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-100' },
  custom: { icon: Sparkles, color: 'text-purple-500', bg: 'bg-purple-100' },
};

/**
 * Single Pot Card Display
 */
const PotCard = ({ pot, totalBalance, onTransfer, isCustomer = true }) => {
  const potConfig = POT_ICONS[pot.pot_type] || POT_ICONS.custom;
  const IconComponent = potConfig.icon;
  const percentage = totalBalance > 0 ? (pot.balance / totalBalance) * 100 : 0;
  
  const isLocked = pot.is_locked || pot.is_time_locked;
  
  return (
    <div 
      className={`relative p-4 rounded-xl border-2 transition-all ${
        isLocked ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`pot-${pot.id}`}
    >
      {isLocked && (
        <div className="absolute top-2 right-2">
          <Lock className="w-4 h-4 text-slate-400" />
        </div>
      )}
      
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${potConfig.bg} flex items-center justify-center`}>
          <IconComponent className={`w-5 h-5 ${potConfig.color}`} />
        </div>
        <div>
          <h4 className="font-semibold text-slate-800">{pot.name}</h4>
          <p className="text-xs text-slate-500">{pot.pot_type}</p>
        </div>
      </div>
      
      <div className="mb-2">
        <p className="text-2xl font-bold text-slate-800">
          ${pot.balance.toFixed(2)}
        </p>
        <p className="text-xs text-slate-500">{percentage.toFixed(0)}% of total</p>
      </div>
      
      <Progress value={percentage} className="h-2 mb-3" />
      
      {pot.is_time_locked && pot.lock_until && (
        <div className="flex items-center gap-1 text-xs text-amber-600 mb-2">
          <Clock className="w-3 h-3" />
          <span>Locked until {new Date(pot.lock_until).toLocaleDateString()}</span>
        </div>
      )}
      
      {isCustomer && !isLocked && pot.balance > 0 && (
        <Button
          size="sm"
          variant="outline"
          className="w-full text-sm"
          onClick={() => onTransfer(pot)}
          data-testid={`transfer-from-${pot.id}`}
        >
          <ArrowRightLeft className="w-4 h-4 mr-1" />
          Transfer
        </Button>
      )}
    </div>
  );
};

/**
 * Main Savings Pots Component
 */
const SavingsPots = ({ token, userId, onBalanceUpdate }) => {
  const [pots, setPots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedPot, setSelectedPot] = useState(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [targetPotId, setTargetPotId] = useState('');
  const [newPotName, setNewPotName] = useState('');
  const [newPotIcon, setNewPotIcon] = useState('💰');
  
  const totalBalance = pots.reduce((sum, pot) => sum + pot.balance, 0);
  
  useEffect(() => {
    fetchPots();
  }, [token]);
  
  const fetchPots = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pots/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPots(data);
      }
    } catch (error) {
      console.error('Failed to fetch pots:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTransfer = async () => {
    if (!selectedPot || !targetPotId || !transferAmount) return;
    
    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > selectedPot.balance) {
      toast.error('Insufficient balance in this pot');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/pots/my/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          from_pot_id: selectedPot.id,
          to_pot_id: targetPotId,
          amount: amount
        })
      });
      
      if (response.ok) {
        toast.success('Transfer successful!');
        setTransferDialogOpen(false);
        setTransferAmount('');
        setTargetPotId('');
        fetchPots();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Transfer failed');
      }
    } catch (error) {
      toast.error('Failed to transfer');
    }
  };
  
  const handleCreatePot = async () => {
    if (!newPotName.trim()) {
      toast.error('Please enter a pot name');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/pots/my`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newPotName,
          icon: newPotIcon,
          pot_type: 'custom'
        })
      });
      
      if (response.ok) {
        toast.success('New pot created!');
        setCreateDialogOpen(false);
        setNewPotName('');
        fetchPots();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create pot');
      }
    } catch (error) {
      toast.error('Failed to create pot');
    }
  };
  
  const openTransferDialog = (pot) => {
    setSelectedPot(pot);
    setTransferDialogOpen(true);
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-pulse">Loading your pots...</div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card data-testid="savings-pots">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-emerald-500" />
            My Money Pots
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Total: ${totalBalance.toFixed(2)}
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid="create-pot-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Pot
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Pot</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Pot Name</Label>
                <Input
                  value={newPotName}
                  onChange={(e) => setNewPotName(e.target.value)}
                  placeholder="e.g., Vacation Fund"
                  data-testid="new-pot-name"
                />
              </div>
              <div>
                <Label>Icon</Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {['💰', '🎮', '🎁', '🏖️', '🎯', '⭐', '🚗', '📱'].map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      className={`p-2 text-2xl rounded-lg border-2 transition-all ${
                        newPotIcon === icon ? 'border-purple-500 bg-purple-50' : 'border-slate-200'
                      }`}
                      onClick={() => setNewPotIcon(icon)}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleCreatePot} className="w-full" data-testid="confirm-create-pot">
                Create Pot
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent>
        {pots.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <PiggyBank className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>No pots yet. Create your first pot!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pots.map((pot) => (
              <PotCard
                key={pot.id}
                pot={pot}
                totalBalance={totalBalance}
                onTransfer={openTransferDialog}
              />
            ))}
          </div>
        )}
      </CardContent>
      
      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer from {selectedPot?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Available: ${selectedPot?.balance.toFixed(2)}</Label>
            </div>
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={selectedPot?.balance}
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                placeholder="0.00"
                data-testid="transfer-amount"
              />
            </div>
            <div>
              <Label>Transfer to</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {pots
                  .filter((p) => p.id !== selectedPot?.id && !p.is_locked && !p.is_time_locked)
                  .map((pot) => (
                    <button
                      key={pot.id}
                      type="button"
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        targetPotId === pot.id ? 'border-purple-500 bg-purple-50' : 'border-slate-200'
                      }`}
                      onClick={() => setTargetPotId(pot.id)}
                    >
                      <span className="text-lg mr-2">{pot.icon}</span>
                      <span className="font-medium">{pot.name}</span>
                    </button>
                  ))}
              </div>
            </div>
            <Button 
              onClick={handleTransfer} 
              className="w-full"
              disabled={!targetPotId || !transferAmount}
              data-testid="confirm-transfer"
            >
              Transfer ${transferAmount || '0.00'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SavingsPots;
